import { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	useChatStoreActions,
	useConversationById,
} from '@atlassian/conversation-assistant/controllers/chat-store';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import type { ThinkingStatus } from '../../pm-plugins/thinking-plugin';
import {
	fireAgentDeactivatedErrorAnalytics,
	fireAgentExternalConfigReferenceErrorAnalytics,
	fireConversationSetupErrorAnalytics,
	fireStreamErrorAnalytics,
} from '../utils/analytics';

import {
	type ContentReviewerAgent,
	useContentReviewerAgentResolver,
} from './useContentReviewerAgentResolver';

/**
 * Builds the ADF prompt that triggers the add-suggestions-to-content skill. The text that
 * precedes the inline skill mention is passed in (already-localised) so the
 * prompt the AI service receives matches the user's locale.
 */
export const buildSuggestEditsPrompt = (precedingText: string): DocNode => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [
				{ type: 'text', text: precedingText },
				{
					type: 'inlineExtension',
					attrs: {
						extensionKey: 'skill:skillTag',
						extensionType: 'com.atlassian.rovo.skill',
						parameters: { slug: 'add-suggestions-to-content' },
					},
				},
			],
		},
	],
});

/**
 * Builds the prompt used once the Content Reviewer agent handles the review. The agent
 * has the add-suggestions-to-content behaviour built in, so the prompt is plain text with
 * no inline skill mention. The text is passed in already-localised.
 */
export const buildAgentReviewPrompt = (text: string): DocNode => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [{ type: 'text', text }],
		},
	],
});

type UseSkillConversationParams = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	isRovoMounted: boolean;
	thinkingStatus: ThinkingStatus | undefined;
};

/**
 * Owns the conversation lifecycle for the add-suggestions-to-content skill triggered by the
 * Thinking surface:
 *  - When `thinkingStatus` flips to `'thinking'`, resolve the Content Reviewer
 *    agent, create an optimistic conversation and dispatch the review prompt at
 *    that agent. When the agent can't be resolved, complete the session without
 *    sending.
 *  - Watch the conversation status and transition the editor state:
 *      idle  → complete (or → stop, if Rovo Chat is open)
 *      error → stop
 *  - When `thinkingStatus` returns to `'idle'`, abort any in-flight request
 *    and clear the active conversation id.
 *  - While Rovo is mounted and we're still thinking, surface the conversation
 *    in the Rovo chat panel via `setSelectedConversation`.
 */
export function useSkill({ api, thinkingStatus, isRovoMounted }: UseSkillConversationParams): void {
	const { formatMessage } = useIntl();
	const {
		createConversationOptimistically,
		sendMessage,
		abortRequests,
		setConversationData,
		setSelectedConversation,
	} = useChatStoreActions();

	// Resolves (and provisions) the agent the prompt is sent at. Stable, so it can't re-trigger
	// the send effect mid-session.
	const { resolveAgent } = useContentReviewerAgentResolver({ api });

	// The legacy route owns its optimistic id. Content Reviewer starts from AI Mate's
	// supported empty slot and records the canonical id minted by the first-message flow.
	const conversationIdRef = useRef<string | null>(null);
	if (
		!fg('content_reviewer_agent') &&
		thinkingStatus === 'thinking' &&
		!conversationIdRef.current
	) {
		conversationIdRef.current = crypto.randomUUID();
	}
	const legacyConversationId = conversationIdRef.current;
	const contentReviewerConversationIdRef = useRef<string | null>(null);
	const [contentReviewerConversationId, setContentReviewerConversationId] = useState<string | null>(
		null,
	);
	const sessionRef = useRef<object | null>(null);
	if (thinkingStatus === 'thinking' && !sessionRef.current) {
		sessionRef.current = {};
	}
	const session = thinkingStatus === 'thinking' ? sessionRef.current : null;

	const activeConversation = useConversationById({
		conversationId: fg('content_reviewer_agent')
			? (contentReviewerConversationId ?? '')
			: (legacyConversationId ?? ''),
	});

	// The stream callbacks are created once at send time but fire later, after Rovo
	// may have mounted/unmounted. Read the latest value through a ref so completion
	// branches on the current mount state (matching the old status-driven effect).
	const isRovoMountedRef = useRef(isRovoMounted);
	isRovoMountedRef.current = isRovoMounted;

	/**
	 * Backend assigned assistant_message_id to be used as
	 * singleInstrumentationId for Unified Instrumentation Events.
	 *
	 * Stub for now until we have unified analytics
	 */
	const [_, setAssistantMessageId] = useState<string | undefined>(undefined);

	// Kick off the add-suggestions-to-content skill once per thinking session.
	useEffect(() => {
		if (!session) {
			return;
		}

		const promptPrefix = formatMessage(aiSuggestionsMessages.suggestEditsPromptPrefix);
		const agentPrompt = formatMessage(aiSuggestionsMessages.contentReviewAgentPrompt);
		let didFireStreamErrorAnalytics = false;
		let cancelled = false;

		/**
		 * Provisioning and resolving the agent are slow, so the session can end while
		 * `run` is still between awaits. Everything that dispatches after an await —
		 * sending the prompt, driving the thinking state — has to check this first.
		 * The ref no longer holding this session's id means the abort effect cleared
		 * it (user stopped) or a later render minted a new one; the flag covers this
		 * effect being torn down, which the ref alone doesn't catch.
		 */
		const isCancelled = (): boolean => cancelled || sessionRef.current !== session;

		const run = async () => {
			let contentReviewerAgent: ContentReviewerAgent | undefined;
			if (fg('content_reviewer_agent')) {
				contentReviewerConversationIdRef.current = EMPTY_CONVERSATION_ID;
				setContentReviewerConversationId(EMPTY_CONVERSATION_ID);
				await createConversationOptimistically({
					id: EMPTY_CONVERSATION_ID,
					status: CHAT_STORE_STATUS.loadingChat,
				});

				if (isCancelled()) {
					return;
				}

				contentReviewerAgent = await resolveAgent();

				// The empty-agent branch below dispatches and then returns, so it never
				// reaches the check before the send — it needs its own.
				if (isCancelled()) {
					return;
				}

				if (!contentReviewerAgent) {
					// No agent to run the skill: clean up the store and show the error screen.
					await setConversationData({
						conversationId: EMPTY_CONVERSATION_ID,
						data: { status: CHAT_STORE_STATUS.idle },
					});
					fireAgentExternalConfigReferenceErrorAnalytics(api?.analytics?.actions);
					api?.aiSuggestions?.actions.setThinkingState('error');
					return;
				}

				await setConversationData({
					conversationId: EMPTY_CONVERSATION_ID,
					data: {
						sourceAgentId: contentReviewerAgent.id,
						agent: {
							agent_id: contentReviewerAgent.id,
							agent_named_id:
								contentReviewerAgent.external_config_reference ?? contentReviewerAgent.named_id,
							agent_name: contentReviewerAgent.name,
							creator_type: contentReviewerAgent.creator_type,
							is_default: contentReviewerAgent.is_default,
							identity_account_id: contentReviewerAgent.identity_account_id ?? undefined,
						},
					},
				});
			} else if (legacyConversationId) {
				await createConversationOptimistically({ id: legacyConversationId });
			}

			if (isCancelled()) {
				return;
			}

			await sendMessage({
				// The agent has the add-suggestions-to-content skill built in, so it only needs a
				// plain review prompt. Without the agent the skill has to be invoked explicitly.
				prompt: fg('content_reviewer_agent')
					? buildAgentReviewPrompt(agentPrompt)
					: buildSuggestEditsPrompt(promptPrefix),
				conversationId: fg('content_reviewer_agent')
					? EMPTY_CONVERSATION_ID
					: (legacyConversationId ?? undefined),
				// If contentReviewerAgent is undefined it won't go to agent
				agent: contentReviewerAgent,
				storeUserMessages: true,
				fastModeEnabled: true,
				addCurrentPageAsContextUserPreference: true,
				/**
				 * This fires when the temporary message id is replaced with the
				 * backend-assigned assistant_message_id.
				 */
				onTempMessageIdReplacedOverride: (_, newId) => {
					setAssistantMessageId(newId);
				},
				...(fg('content_reviewer_agent')
					? {
							onNewConversationIdAvailable: (conversationId: string) => {
								if (isCancelled()) {
									return;
								}
								contentReviewerConversationIdRef.current = conversationId;
								setContentReviewerConversationId(conversationId);
							},
						}
					: {}),
				promptStreamHook: {
					/**
					 * Fires when the FINAL_RESPONSE stream chunk is processed — the
					 * add-suggestions-to-content skill has produced its answer.
					 *   - Rovo mounted   → setThinkingState('idle') (Rovo chat takes over, no bar)
					 *   - Rovo unmounted → refreshSuggestions then setThinkingState('complete')
					 */
					onFinalResponse: () => {
						if (isRovoMountedRef.current) {
							api?.aiSuggestions?.actions.setThinkingState('idle');
						} else {
							api?.aiSuggestions?.actions.refreshSuggestions();
							api?.aiSuggestions?.actions.setThinkingState('complete');
						}
						return Promise.resolve();
					},
					/**
					 * Fires on an in-stream ERROR / ACTION_ERROR chunk.
					 */
					onError: (error) => {
						didFireStreamErrorAnalytics = true;
						fireStreamErrorAnalytics(api?.analytics?.actions, error);
						api?.aiSuggestions?.actions.setThinkingState('error');
						return Promise.resolve();
					},
				},
			});
		};

		run().catch((error) => {
			// Stopping the session aborts the in-flight request, so the rejection that
			// follows is the expected outcome of a user cancellation rather than a
			// failure worth reporting — and stopping again would end whichever session
			// is current by now.
			if (isCancelled()) {
				return;
			}
			if (!didFireStreamErrorAnalytics) {
				fireConversationSetupErrorAnalytics(api?.analytics?.actions, error);
			}
			if (fg('content_reviewer_agent')) {
				setConversationData({
					conversationId: contentReviewerConversationIdRef.current ?? EMPTY_CONVERSATION_ID,
					data: { status: CHAT_STORE_STATUS.idle },
				});
			}
			api?.aiSuggestions?.actions.setThinkingState('error');
		});

		return () => {
			cancelled = true;
		};
	}, [
		session,
		api,
		createConversationOptimistically,
		legacyConversationId,
		sendMessage,
		setConversationData,
		formatMessage,
		resolveAgent,
	]);

	// Clear the conversation id (and abort any in-flight request) whenever a
	// thinking session ends (both 'idle' and 'complete'). Clearing on 'complete'
	// lets a second "Review" mint a fresh id and re-trigger the skill — otherwise
	// it reused the stale id and did nothing.
	useEffect(() => {
		if (thinkingStatus === 'thinking') {
			return;
		}
		const conversationId = fg('content_reviewer_agent')
			? contentReviewerConversationIdRef.current
			: conversationIdRef.current;
		if (conversationId) {
			abortRequests?.({ conversationId });
		}
		conversationIdRef.current = null;
		contentReviewerConversationIdRef.current = null;
		setContentReviewerConversationId(null);
		sessionRef.current = null;
		setAssistantMessageId(undefined);
	}, [thinkingStatus, abortRequests]);

	// React to conversation status changes reported by the store and drive the
	// editor thinking state accordingly:
	//   - store idle + Rovo mounted   → setThinkingState('idle') (Rovo chat takes over)
	//   - store idle + Rovo unmounted → setThinkingState('complete')
	//   - deactivatedAgentError       → setThinkingState('error')
	useEffect(() => {
		/**
		 * Note - because promptStreamHook already handles onFinalResponse,
		 * this hook shouldn't need to do anything unless the conversation
		 * somehow terminates without a final response (e.g. deactivatedAgentError)
		 * or has an error that isn't caught by onError.
		 */
		if (!activeConversation) {
			return;
		}
		if (
			fg('content_reviewer_agent') &&
			activeConversation.id !== contentReviewerConversationIdRef.current
		) {
			return;
		}

		switch (activeConversation.status) {
			case CHAT_STORE_STATUS.idle: {
				// Returning to idle mid-thinking means the session finished. The first
				// idle has lastAgentMessageStatus undefined; once set, it's done.
				if (
					thinkingStatus === 'thinking' &&
					activeConversation.lastAgentMessageStatus !== undefined
				) {
					// FAILED/CANCELLED means no suggestions — show the error screen
					// instead of completing (which would overwrite the error state).
					const lastAgentMessageFailed =
						activeConversation.lastAgentMessageStatus === 'FAILED' ||
						activeConversation.lastAgentMessageStatus === 'CANCELLED';

					if (lastAgentMessageFailed) {
						api?.aiSuggestions?.actions.setThinkingState('error');
					} else if (isRovoMountedRef.current) {
						// Rovo chat is open, so don't show the thinking bar on completion.
						api?.aiSuggestions?.actions.setThinkingState('idle');
					} else {
						api?.aiSuggestions?.actions.refreshSuggestions();
						api?.aiSuggestions?.actions.setThinkingState('complete');
					}
				} else if (
					thinkingStatus === 'thinking' &&
					!fg('content_reviewer_agent') &&
					isRovoMountedRef.current
				) {
					api?.aiSuggestions?.actions.setThinkingState('idle');
				}
				break;
			}
			case CHAT_STORE_STATUS.deactivatedAgentError: {
				fireAgentDeactivatedErrorAnalytics(api?.analytics?.actions);
				api?.aiSuggestions?.actions.setThinkingState('error');
				break;
			}
		}
	}, [activeConversation, api, thinkingStatus]);

	// When Rovo chat is open and a conversation is active, surface it in the
	// Rovo panel so the user can follow along in real time.
	useEffect(() => {
		const conversationId = fg('content_reviewer_agent')
			? contentReviewerConversationId
			: legacyConversationId;
		if (isRovoMounted && thinkingStatus !== 'idle' && conversationId) {
			setSelectedConversation({ conversationId });
		}
	}, [
		contentReviewerConversationId,
		isRovoMounted,
		legacyConversationId,
		setSelectedConversation,
		thinkingStatus,
	]);
}
